/**
 * PaletteController — the ONE owner of the colour palette: which *type* is active
 * (number row 6–0) and each type's remembered **base hue**.
 *
 * Selection model (like the base-visual selectors): pressing 6–0 exclusively
 * activates that palette type — `select()` writes `palette.type` and mirrors that
 * type's stored base hue to `palette.hue`. The shader reads `uPaletteType` /
 * `uBaseHue` and builds the ramp from them.
 *
 * Base-hue model: while a palette key is held, −/+ shift *that type's* base hue
 * around the wheel. A tap nudges it one `TAP_STEP`; holding past `HOLD_DELAY_MS`
 * begins a continuous cycle (`tick`, per-frame, like the modifier scrub). Hue
 * wraps smoothly with no end stops. Each type keeps its own hue, so leaving and
 * returning restores it. −/+ with no palette key held are ignored here — they stay
 * with ModifierController's per-effect knob.
 *
 * Same seam discipline as the rest of the input layer: it observes the normalized
 * key stream and writes only its own bus keys; it never touches the renderer.
 */

import { ParamBus } from '../state/paramBus';
import {
  PaletteDef,
  paletteByKey,
  paletteConfig,
  PALETTE_TYPE_PARAM,
  PALETTE_HUE_PARAM,
  DEFAULT_PALETTE_INDEX,
} from '../effects/presetConfig';
import { PLUS_KEYS, MINUS_KEYS } from './modifierController';

/** One tap of −/+ shifts the base hue this far (1 == full wheel) — ~7.5°. */
export const TAP_STEP = 1 / 48;
/** Hold −/+ longer than this (ms) before the continuous cycle kicks in. */
export const HOLD_DELAY_MS = 250;
/** Continuous cycle speed while held: full wheel in ~3s. */
export const CYCLE_RATE = 1 / 3;

export class PaletteController {
  /** Active type index, mirrored to `palette.type`. */
  private activeType = DEFAULT_PALETTE_INDEX;
  /** Remembered base hue per type index (0..1). */
  private readonly hues: number[] = [];
  /** Held palette-type indices, oldest → newest; the last is the −/+ target. */
  private heldTypes: number[] = [];
  /** Currently-held −/+ key codes. */
  private readonly modKeys = new Set<string>();
  /** When the current −/+ hold began (for hold detection); -1 == not ramping. */
  private rampStartMs = -1;
  /** -1 until the first tick seeds the timebase. */
  private lastMs = -1;

  constructor(
    private readonly bus: ParamBus,
    private readonly palettes: ReadonlyMap<string, PaletteDef> = paletteByKey,
    private readonly random: () => number = Math.random,
  ) {
    for (const p of paletteConfig) this.hues[p.index] = p.defaultHue;
  }

  /** Feed a normalized key-down (same identity KeyState/InputController use). */
  keyDown(key: string, nowMs: number): void {
    if (PLUS_KEYS.includes(key) || MINUS_KEYS.includes(key)) {
      this.modKeys.add(key);
      // Only capture −/+ while a palette key is held; otherwise leave them alone.
      const target = this.target;
      if (target === undefined) return;
      const dir = this.direction();
      if (dir !== 0) {
        this.adjustHue(target, dir * TAP_STEP); // immediate tap
        this.rampStartMs = nowMs; // start the hold clock
      }
      return;
    }
    const def = this.palettes.get(key);
    if (!def) return;
    // Move to the top of the held stack so it becomes the active/target type.
    this.heldTypes = this.heldTypes.filter((t) => t !== def.index);
    this.heldTypes.push(def.index);
    this.select(def.index);
    // If −/+ is already held, begin ramping this freshly-held type.
    if (this.direction() !== 0) this.rampStartMs = nowMs;
  }

  /** Feed a normalized key-up. */
  keyUp(key: string): void {
    if (this.modKeys.delete(key)) {
      if (this.direction() === 0) this.rampStartMs = -1;
      return;
    }
    const def = this.palettes.get(key);
    if (!def) return;
    const wasTarget = this.target;
    this.heldTypes = this.heldTypes.filter((t) => t !== def.index);
    const nowTarget = this.target;
    // Following a still-held palette key keeps the shown/tuned type consistent.
    if (nowTarget !== undefined && nowTarget !== wasTarget) this.select(nowTarget);
  }

  /** The type −/+ would tune right now — the most recently pressed held key. */
  get target(): number | undefined {
    return this.heldTypes[this.heldTypes.length - 1];
  }

  /** True while any palette key is held (main.ts uses it to yield the modifier). */
  get hasHeldPaletteKey(): boolean {
    return this.heldTypes.length > 0;
  }

  /** Advance the continuous hue cycle while −/+ is held past the tap threshold. */
  tick(nowMs: number): void {
    const dt = this.lastMs < 0 ? 0 : (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;

    const dir = this.direction();
    const target = this.target;
    if (dir === 0 || target === undefined || this.rampStartMs < 0) return;
    if (nowMs - this.rampStartMs < HOLD_DELAY_MS || dt <= 0) return;
    this.adjustHue(target, dir * CYCLE_RATE * dt);
  }

  /** Exclusively activate a palette type and surface its remembered base hue. */
  select(type: number): void {
    this.activeType = type;
    this.bus.set(PALETTE_TYPE_PARAM, type);
    this.bus.set(PALETTE_HUE_PARAM, this.hues[type]);
  }

  /** Tab reroll: pick a random type, keeping each type's remembered hue. */
  randomize(): void {
    this.select(Math.floor(this.random() * paletteConfig.length));
  }

  /** Step the active palette type by `dir` (±1), wrapping — the swipe-up/down gesture. */
  step(dir: number): void {
    const len = paletteConfig.length;
    this.select((((this.activeType + dir) % len) + len) % len);
  }

  /** Boot / Escape: return to the default palette type (Spectrum). */
  reset(): void {
    this.select(DEFAULT_PALETTE_INDEX);
  }

  /** Shift a type's base hue by `delta`, wrapping the wheel; mirror if active. */
  private adjustHue(type: number, delta: number): void {
    this.hues[type] = round4(wrap01(this.hues[type] + delta));
    if (type === this.activeType) this.bus.set(PALETTE_HUE_PARAM, this.hues[type]);
  }

  /** +1 while a plus key is held, -1 for minus, 0 for neither/both. */
  private direction(): number {
    const up = PLUS_KEYS.some((k) => this.modKeys.has(k));
    const down = MINUS_KEYS.some((k) => this.modKeys.has(k));
    return (up ? 1 : 0) - (down ? 1 : 0);
  }
}

function wrap01(v: number): number {
  return ((v % 1) + 1) % 1;
}

function round4(v: number): number {
  return Math.round(v * 10000) / 10000;
}
